import { IonButton, IonContent, IonFooter, IonIcon, IonSkeletonText, IonTextarea } from '@ionic/react';
import { arrowUpOutline, checkmarkOutline, chevronForward, sparklesOutline, stopOutline } from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import TokenText from '../../components/TokenText';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';
import { TransitPolicy } from '../../models/TransitPolicy';
import {
    cancelDoorPolicyAssistantMessage,
    createDoorPolicyAssistantStreamId,
    sendDoorPolicyAssistantMessage,
    useDoorPolicySummaryQuery,
    type DoorPolicyAssistantHistoryMessage,
    type DoorPolicyAssistantProposal,
    type DoorPolicyAssistantQuestion
} from '../../query/doorPolicyAssistantQuery';
import { stripTokenText } from '../../utils/tokenText';
import styles from './DoorPolicyAssistantView.module.css';

/** `revising`: the user tapped Not quite; it stays the base for refinements until replaced. */
type ProposalState = 'pending' | 'revising' | 'applied' | 'superseded';

type ThreadItem =
    | { id: number; kind: 'user'; text: string }
    | {
          id: number;
          kind: 'assistant';
          text: string;
          streaming: boolean;
          question?: DoorPolicyAssistantQuestion;
          proposal?: DoorPolicyAssistantProposal;
          proposalState?: ProposalState;
          error?: string;
      }
    | { id: number; kind: 'applied'; undone: boolean };

interface DoorPolicyAssistantViewProps {
    deviceId: string;
    policyName: string;
    transitPolicy?: TransitPolicy;
    /** The editor holds unsaved manual edits, so the summary describes a draft. */
    edited: boolean;
    /** Another segment is showing; the thread stays mounted so it survives tab switches. */
    hidden: boolean;
    onApplyProposal: (proposal: DoorPolicyAssistantProposal) => void;
    onUndoApply: () => void;
    onShowRules: () => void;
}

const MAX_MESSAGE_LENGTH = 500;

/** Case- and punctuation-insensitive containment check for duplicate question text. */
const normaliseForCompare = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/** Policy summaries come one point per line; several lines read as a list. */
const PolicySummary: React.FC<{ text: string; deviceId: string }> = ({ text, deviceId }) => {
    const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    if (lines.length < 2) {
        return (
            <p className={styles.bubbleText}>
                <TokenText text={text.trim()} deviceId={deviceId} />
            </p>
        );
    }
    return (
        <ul className={styles.summaryList}>
            {lines.map((line, index) => (
                <li key={index} className={styles.bubbleText}>
                    <TokenText text={line} deviceId={deviceId} />
                </li>
            ))}
        </ul>
    );
};

/**
 * The Assistant segment of the door policy editor (design: "Door Policy
 * Assistant Treatments", A - Chat-first). A summary of the current draft opens
 * the thread; requests stream back as text, optionally with a clarifying
 * question or a proposal card. Apply only changes the editor's draft - the
 * existing Save button persists it.
 */
const DoorPolicyAssistantView: React.FC<DoorPolicyAssistantViewProps> = ({
    deviceId,
    policyName,
    transitPolicy,
    edited,
    hidden,
    onApplyProposal,
    onUndoApply,
    onShowRules
}) => {
    const { t: tModals, i18n } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const [items, setItems] = useState<ThreadItem[]>([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const streamIdRef = useRef<string | undefined>(undefined);
    const nextItemIdRef = useRef(1);
    const nextItemId = () => nextItemIdRef.current++;
    const contentRef = useRef<HTMLIonContentElement | null>(null);
    const locale = i18n.language;

    const summaryQuery = useDoorPolicySummaryQuery({
        deviceId,
        policyName,
        transitPolicy,
        locale,
        enabled: !hidden
    });

    const scrollToBottom = useCallback((duration = 250) => {
        contentRef.current?.scrollToBottom(duration);
    }, []);

    // Every thread update (new item, streamed delta, final reply swapping in
    // a card) should keep the latest content in view.
    useEffect(() => {
        if (!hidden) {
            scrollToBottom(items.length ? 150 : 0);
        }
    }, [items, hidden, scrollToBottom]);

    const updateItem = (id: number, update: (item: Extract<ThreadItem, { kind: 'assistant' }>) => ThreadItem) => {
        setItems((prev) => prev.map((item) => (item.id === id && item.kind === 'assistant' ? update(item) : item)));
    };

    const buildHistory = (thread: ThreadItem[]): DoorPolicyAssistantHistoryMessage[] =>
        thread.flatMap((item): DoorPolicyAssistantHistoryMessage[] => {
            if (item.kind === 'user') {
                return [{ role: 'user', content: item.text }];
            }
            if (item.kind !== 'assistant' || item.error) {
                return [];
            }
            const parts = [item.text];
            if (item.question) {
                parts.push(`[Asked: ${stripTokenText(item.question.question)}]`);
            }
            if (item.proposal) {
                parts.push(`[Proposed a change (${item.proposalState}): ${stripTokenText(item.proposal.changes)}]`);
            }
            const content = parts.filter(Boolean).join('\n');
            return content ? [{ role: 'assistant', content }] : [];
        });

    const pendingProposalOf = (thread: ThreadItem[]) => {
        for (let i = thread.length - 1; i >= 0; i--) {
            const item = thread[i];
            if (item.kind === 'assistant' && item.proposal && (item.proposalState === 'pending' || item.proposalState === 'revising')) {
                return item.proposal;
            }
        }
        return undefined;
    };

    /** Resolves true once the assistant replied (false if nothing was sent, or it failed or was cancelled). */
    const send = async (rawText: string, options: { notQuite?: boolean } = {}): Promise<boolean> => {
        const text = rawText.trim();
        if (!text || busy || !transitPolicy) {
            return false;
        }

        const userId = nextItemId();
        const assistantId = nextItemId();
        const history = [...buildHistory(items), { role: 'user' as const, content: text }];
        const pendingProposal = pendingProposalOf(items);

        setDraft('');
        setItems((prev) => [...prev, { id: userId, kind: 'user', text }, { id: assistantId, kind: 'assistant', text: '', streaming: true }]);
        setBusy(true);

        const streamId = createDoorPolicyAssistantStreamId();
        streamIdRef.current = streamId;

        try {
            const reply = await sendDoorPolicyAssistantMessage(
                {
                    deviceId,
                    policyName,
                    draftPolicy: transitPolicy,
                    pendingProposal: pendingProposal?.transitPolicy,
                    messages: history,
                    locale,
                    notQuite: options.notQuite
                },
                {
                    onTextDelta: (delta) => {
                        updateItem(assistantId, (item) => ({ ...item, text: item.text + delta }));
                        scrollToBottom(0);
                    },
                    onReset: () => updateItem(assistantId, (item) => ({ ...item, text: '' }))
                },
                streamId
            );

            setItems((prev) =>
                prev.map((item) => {
                    if (item.id === assistantId && item.kind === 'assistant') {
                        return {
                            ...item,
                            text: reply.text,
                            streaming: false,
                            question: reply.question,
                            proposal: reply.proposal,
                            proposalState: reply.proposal ? 'pending' : undefined,
                            error: reply.error ? tModals('door_policy.assistant.error_invalid_proposal') : undefined
                        };
                    }
                    // A new proposal replaces any earlier one still awaiting a decision.
                    if (reply.proposal && item.kind === 'assistant' && item.proposal && (item.proposalState === 'pending' || item.proposalState === 'revising')) {
                        return { ...item, proposalState: 'superseded' };
                    }
                    return item;
                })
            );
            return true;
        } catch (error: any) {
            if (error?.code === 499) {
                setItems((prev) => prev.filter((item) => item.id !== assistantId));
            } else {
                const message =
                    error?.code === 429
                        ? tModals('door_policy.assistant.error_rate_limited')
                        : tModals('door_policy.assistant.error_generic');
                updateItem(assistantId, (item) => ({ ...item, streaming: false, error: message }));
            }
            return false;
        } finally {
            setBusy(false);
            streamIdRef.current = undefined;
        }
    };

    const cancel = () => {
        if (streamIdRef.current) {
            cancelDoorPolicyAssistantMessage(streamIdRef.current);
        }
    };

    const applyProposal = (itemId: number) => {
        const item = items.find((candidate) => candidate.id === itemId);
        if (!item || item.kind !== 'assistant' || !item.proposal || item.proposalState !== 'pending') {
            return;
        }
        onApplyProposal(item.proposal);
        // The applied row lands after the parent re-renders; settle, then reveal it.
        setTimeout(() => scrollToBottom(200), 350);
        setItems((prev) => [
            ...prev.map((candidate) => (candidate.id === itemId && candidate.kind === 'assistant' ? { ...candidate, proposalState: 'applied' as const } : candidate)),
            { id: nextItemId(), kind: 'applied', undone: false }
        ]);
    };

    const undoApply = (appliedId: number) => {
        onUndoApply();
        setItems((prev) => {
            const appliedIndex = prev.findIndex((item) => item.id === appliedId);
            return prev.map((item, index) => {
                if (item.id === appliedId && item.kind === 'applied') {
                    return { ...item, undone: true };
                }
                // Reopen the proposal this row confirmed so it can be applied again.
                if (index < appliedIndex && item.kind === 'assistant' && item.proposalState === 'applied') {
                    return { ...item, proposalState: 'pending' as const };
                }
                return item;
            });
        });
    };

    const setProposalState = (itemId: number, from: ProposalState, to: ProposalState) => {
        setItems((prev) =>
            prev.map((item) => (item.id === itemId && item.kind === 'assistant' && item.proposalState === from ? { ...item, proposalState: to } : item))
        );
    };

    // Settle the card and say so in the thread; the assistant replies by asking what to change.
    const notQuite = async (itemId: number) => {
        if (busy) {
            return;
        }
        setProposalState(itemId, 'pending', 'revising');
        if (!(await send(tModals('door_policy.assistant.not_quite'), { notQuite: true }))) {
            setProposalState(itemId, 'revising', 'pending');
        }
    };

    const lastItem = items[items.length - 1];
    const showIntro = items.length === 0;
    // Policy-specific prompts generated with the summary. Placeholder data belongs to the
    // previous draft, so its suggestions may no longer apply: skeleton until the new ones land.
    const suggestionsLoading = summaryQuery.isPending || summaryQuery.isPlaceholderData;
    const suggestions = suggestionsLoading ? [] : (summaryQuery.data?.suggestions ?? []);

    const hiddenStyle = hidden ? { display: 'none' } : undefined;

    return (
        <>
            <IonContent ref={contentRef} color="light" fullscreen className={styles.content} style={hiddenStyle}>
                <div className={styles.thread}>
                    <div className={styles.assistantRow}>
                        <span className={styles.avatar} aria-hidden="true">
                            <IonIcon icon={sparklesOutline} />
                        </span>
                        <div className={`${styles.bubble} ${styles.bubbleWide}`}>
                            <h3 className={styles.bubbleTitle}>
                                {edited ? tModals('door_policy.assistant.summary_unsaved_title') : tModals('door_policy.assistant.summary_title')}
                            </h3>
                            {summaryQuery.data ? (
                                <PolicySummary text={summaryQuery.data.summary} deviceId={deviceId} />
                            ) : summaryQuery.isError ? (
                                <p className={`${styles.bubbleText} ${styles.muted}`}>
                                    {tModals('door_policy.assistant.summary_error')}{' '}
                                    <button type="button" className={styles.inlineLink} onClick={() => summaryQuery.refetch()}>
                                        {tCommon('buttons.retry')}
                                    </button>
                                </p>
                            ) : (
                                <div className={styles.skeleton}>
                                    <IonSkeletonText animated style={{ width: '92%' }} />
                                    <IonSkeletonText animated style={{ width: '84%' }} />
                                    <IonSkeletonText animated style={{ width: '60%' }} />
                                </div>
                            )}
                        </div>
                    </div>

                    {showIntro && (
                        <>
                            <div className={styles.assistantRow}>
                                <span className={styles.avatar} aria-hidden="true">
                                    <IonIcon icon={sparklesOutline} />
                                </span>
                                <div className={`${styles.bubble} ${styles.bubbleWide}`}>
                                    <p className={styles.bubbleText}>{tModals('door_policy.assistant.intro')}</p>
                                </div>
                            </div>
                            {suggestionsLoading ? (
                                <div className={styles.chips} aria-hidden="true">
                                    {['8.5em', '10em', '7em'].map((width) => (
                                        <IonSkeletonText key={width} animated className={styles.chipSkeleton} style={{ width }} />
                                    ))}
                                </div>
                            ) : (
                                suggestions.length > 0 && (
                                    <div className={styles.chips}>
                                        {suggestions.map((suggestion) => (
                                            <button key={suggestion} type="button" className={styles.chip} onClick={() => send(suggestion)} disabled={!transitPolicy}>
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                )
                            )}
                        </>
                    )}

                    {items.map((item) => {
                        if (item.kind === 'user') {
                            return (
                                <div key={item.id} className={styles.userRow}>
                                    <div className={styles.userBubble}>{item.text}</div>
                                </div>
                            );
                        }

                        if (item.kind === 'applied') {
                            return (
                                <div key={item.id} className={`${styles.appliedRow} ${item.undone ? styles.appliedRowUndone : ''}`}>
                                    <IonIcon icon={checkmarkOutline} className={styles.appliedIcon} aria-hidden="true" />
                                    <span className={styles.appliedText}>
                                        {item.undone ? (
                                            tModals('door_policy.assistant.undone')
                                        ) : (
                                            <Trans i18nKey="door_policy.assistant.applied" ns="modals" components={{ strong: <strong /> }} />
                                        )}
                                        {!item.undone && (
                                            <>
                                                {' '}
                                                <button type="button" className={styles.inlineLink} onClick={onShowRules}>
                                                    {tModals('door_policy.assistant.see_in_rules')}
                                                    <IonIcon icon={chevronForward} aria-hidden="true" />
                                                </button>
                                            </>
                                        )}
                                    </span>
                                    {!item.undone && (
                                        <button type="button" className={styles.undoButton} onClick={() => undoApply(item.id)}>
                                            {tModals('door_policy.assistant.undo')}
                                        </button>
                                    )}
                                </div>
                            );
                        }

                        const showQuestionOptions = item === lastItem && !!item.question?.options.length && !busy;
                        const proposal = item.proposal;
                        const proposalState = item.proposalState ?? 'pending';
                        // Models often repeat the question in the reply text; show it once.
                        const showQuestionBubble =
                            !!item.question && !!item.text && !normaliseForCompare(item.text).includes(normaliseForCompare(item.question.question));
                        // Replies often carry only a tool call: a question without text leads with the question,
                        // and a proposal card without anything above it takes the avatar itself.
                        const showLeadBubble = !!(item.text || item.streaming || item.error || item.question);

                        return (
                            <div key={item.id} className={styles.assistantGroup}>
                                {showLeadBubble && (
                                    <div className={styles.assistantRow}>
                                        <span className={styles.avatar} aria-hidden="true">
                                            <IonIcon icon={sparklesOutline} />
                                        </span>
                                        <div className={styles.bubble}>
                                            {item.streaming && !item.text ? (
                                                <span className={styles.typing} aria-label={tModals('door_policy.assistant.thinking')}>
                                                    <span />
                                                    <span />
                                                    <span />
                                                </span>
                                            ) : (
                                                <p className={styles.bubbleText}>
                                                    <TokenText text={item.text} deviceId={deviceId} streaming={item.streaming} />
                                                    {item.question && !item.text && <TokenText text={item.question.question} deviceId={deviceId} />}
                                                </p>
                                            )}
                                            {item.error && <p className={`${styles.bubbleText} ${styles.errorText}`}>{item.error}</p>}
                                        </div>
                                    </div>
                                )}
                                {showQuestionBubble && item.question && (
                                    <div className={styles.assistantRow}>
                                        <span className={styles.avatarSpacer} aria-hidden="true" />
                                        <div className={styles.bubble}>
                                            <p className={styles.bubbleText}>
                                                <TokenText text={item.question.question} deviceId={deviceId} />
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {showQuestionOptions && (
                                    <div className={`${styles.chips} ${styles.chipsRight}`}>
                                        {item.question!.options.map((option) => (
                                            <button key={option} type="button" className={styles.chip} onClick={() => send(stripTokenText(option))}>
                                                <TokenText text={option} deviceId={deviceId} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {proposal && (
                                    <div className={styles.assistantRow}>
                                        {showLeadBubble ? (
                                            <span className={styles.avatarSpacer} aria-hidden="true" />
                                        ) : (
                                            <span className={styles.avatar} aria-hidden="true">
                                                <IonIcon icon={sparklesOutline} />
                                            </span>
                                        )}
                                        <div className={`${styles.proposalCard} ${proposalState !== 'pending' ? styles.proposalCardSettled : ''}`}>
                                            <div className={styles.proposalHeader}>
                                                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                                                <span className={styles.proposalKicker}>{tModals('door_policy.assistant.proposed_change')}</span>
                                                <span className={styles.proposalStatus}>
                                                    {proposalState === 'pending'
                                                        ? tModals('door_policy.assistant.not_saved')
                                                        : proposalState === 'revising'
                                                          ? tModals('door_policy.assistant.revising')
                                                          : proposalState === 'applied'
                                                            ? tModals('door_policy.assistant.applied_label')
                                                            : tModals('door_policy.assistant.superseded')}
                                                </span>
                                            </div>
                                            <div className={styles.proposalBody}>
                                                <div className={styles.proposalSectionLabel}>{tModals('door_policy.assistant.whats_new')}</div>
                                                <p className={styles.bubbleText}>
                                                    <TokenText text={proposal.changes} deviceId={deviceId} />
                                                </p>
                                                <div className={`${styles.proposalSectionLabel} ${styles.proposalSectionLabelSpaced}`}>
                                                    {tModals('door_policy.assistant.new_policy')}
                                                </div>
                                                <PolicySummary text={proposal.summary} deviceId={deviceId} />
                                                {proposal.name && (
                                                    <p className={`${styles.bubbleText} ${styles.muted}`}>
                                                        {tModals('door_policy.assistant.renames_to', { name: proposal.name })}
                                                    </p>
                                                )}
                                            </div>
                                            {proposalState === 'pending' && (
                                                <div className={styles.proposalActions}>
                                                    <IonButton className={styles.applyButton} expand="block" onClick={() => applyProposal(item.id)} disabled={busy}>
                                                        {tModals('door_policy.assistant.apply')}
                                                    </IonButton>
                                                    <IonButton className={styles.notQuiteButton} fill="outline" color="medium" onClick={() => notQuite(item.id)} disabled={busy}>
                                                        {tModals('door_policy.assistant.not_quite')}
                                                    </IonButton>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <p className={styles.disclaimer}>{tModals('door_policy.assistant.disclaimer')}</p>
                </div>
            </IonContent>
            <IonFooter className="ion-no-border" style={hiddenStyle}>
                <div className={styles.composer}>
                    <IonTextarea
                        className={styles.composerField}
                        value={draft}
                        placeholder={tModals('door_policy.assistant.composer_placeholder')}
                        rows={1}
                        autoGrow
                        maxlength={MAX_MESSAGE_LENGTH}
                        enterkeyhint="send"
                        disabled={!transitPolicy}
                        onIonInput={(e) => setDraft(e.detail.value ?? '')}
                        onKeyDown={(e) => {
                            // Desktop convenience; on touch keyboards Enter inserts a newline as usual.
                            if (e.key === 'Enter' && !e.shiftKey && window.matchMedia('(hover: hover)').matches) {
                                e.preventDefault();
                                send(draft);
                            }
                        }}
                    />
                    {busy ? (
                        <IonButton className={styles.sendButton} shape="round" fill="solid" color="medium" onClick={cancel} aria-label={tModals('door_policy.assistant.stop')}>
                            <IonIcon slot="icon-only" icon={stopOutline} />
                        </IonButton>
                    ) : (
                        <IonButton
                            className={styles.sendButton}
                            shape="round"
                            fill="solid"
                            disabled={!draft.trim() || !transitPolicy}
                            onClick={() => send(draft)}
                            aria-label={tModals('door_policy.assistant.send')}
                        >
                            <IonIcon slot="icon-only" icon={arrowUpOutline} />
                        </IonButton>
                    )}
                </div>
            </IonFooter>
        </>
    );
};

export default DoorPolicyAssistantView;
